import { CompleteRegistrationRequest, userInvitationService, VerifyInvitationResponse } from '@/services/user-invitation.service';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import {
  AlertCircle,
  ArrowRight,
  CheckCircle,
  Clock,
  Eye,
  EyeOff,
  Loader2,
  Mail,
  Phone,
  Shield,
  User,
  UserCheck,
  Users
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import * as yup from 'yup';

const inviteSchema = yup.object().shape({
  password: yup.string()
    .min(8, 'Password must be at least 8 characters')
    .matches(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .matches(/[a-z]/, 'Password must contain at least one lowercase letter')
    .matches(/\d/, 'Password must contain at least one number')
    .matches(/[@$!%*?&]/, 'Password must contain at least one special character')
    .required('Password is required'),
  confirm_password: yup.string()
    .oneOf([yup.ref('password')], 'Passwords must match')
    .required('Please confirm your password'),
  mobile_number: yup.string().optional(),
  security_question: yup.string().optional(),
  security_answer: yup.string().when('security_question', {
    is: (val: string) => val && val.length > 0,
    then: (schema) => schema.required('Security answer is required when a question is selected'),
    otherwise: (schema) => schema.optional(),
  }),
});

type InviteFormData = {
  password: string;
  confirm_password: string;
  mobile_number?: string;
  security_question?: string;
  security_answer?: string;
};

export default function RegisterInvite() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [invitationData, setInvitationData] = useState<VerifyInvitationResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [tokenError, setTokenError] = useState<string | null>(null);
  const [acceptanceStatus, setAcceptanceStatus] = useState<'pending' | 'accepted' | 'registered' | null>(null);
  const [passwordStrength, setPasswordStrength] = useState(0);
  
  const token = searchParams.get('token');

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm<InviteFormData>({
    resolver: yupResolver(inviteSchema),
  });

  const password = watch('password');

  // Security questions
  const securityQuestions = [
    "What was the name of your first pet?",
    "What is your mother's maiden name?",
    "What was the name of your first school?",
    "What is your favorite book?",
    "What city were you born in?",
    "What was your childhood nickname?",
    "What is the name of your favorite teacher?",
    "What was the model of your first car?",
  ];

  // Verify token on mount
  useEffect(() => {
    if (!token) {
      setTokenError('No invitation token provided');
      setLoading(false);
      return;
    }

    verifyInvitation();
  }, [token]);

  // Calculate password strength
  useEffect(() => {
    if (!password) {
      setPasswordStrength(0);
      return;
    }

    let strength = 0;
    if (password.length >= 8) strength += 20;
    if (password.length >= 12) strength += 10;
    if (/[a-z]/.test(password)) strength += 20;
    if (/[A-Z]/.test(password)) strength += 20;
    if (/\d/.test(password)) strength += 20;
    if (/[@$!%*?&]/.test(password)) strength += 10;

    setPasswordStrength(strength);
  }, [password]);

  const verifyInvitation = async () => {
    if (!token) return;

    try {
      setLoading(true);
      
      // First check the status
      const statusResponse = await userInvitationService.checkInvitationStatus(token);
      
      if (statusResponse.success && statusResponse.data) {
        if (statusResponse.data.status === 'expired') {
          setTokenError('This invitation has expired. Please contact your administrator for a new invitation.');
          setLoading(false);
          return;
        }
        
        if (statusResponse.data.status === 'registered') {
          setTokenError('This invitation has already been used. Please login with your credentials.');
          setLoading(false);
          return;
        }
      }

      // Verify the invitation details
      const response = await userInvitationService.verifyInvitation(token);
      
      if (response.success && response.data) {
        setInvitationData(response.data);
        setAcceptanceStatus(response.data.status as any);
        
        // If not yet accepted, accept it automatically
        if (response.data.status === 'pending') {
          await acceptInvitation();
        }
      } else {
        const errorMessage = response.error?.error_description ||
                           response.error?.message ||
                           response.description ||
                           'Invalid invitation token';
        setTokenError(errorMessage);
      }
    } catch (error: any) {
      console.error('Error verifying invitation:', error);
      const errorMessage = error?.response?.data?.error?.error_description ||
                          error?.response?.data?.error?.message ||
                          error?.response?.data?.error_description ||
                          error?.response?.data?.message ||
                          error?.message ||
                          'Failed to verify invitation. Please try again or contact support.';
      setTokenError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const acceptInvitation = async () => {
    if (!token) return;

    try {
      const response = await userInvitationService.acceptInvitation({ token });
      
      if (response.success) {
        setAcceptanceStatus('accepted');
        toast.success('Invitation accepted! Please complete your registration.');
      } else {
        const errorMessage = response.error?.error_description || 
                           response.error?.message || 
                           response.description ||
                           'Failed to accept invitation';
        toast.error(errorMessage);
      }
    } catch (error: any) {
      console.error('Error accepting invitation:', error);
      const errorMessage = error?.response?.data?.error?.error_description ||
                          error?.response?.data?.error?.message ||
                          error?.response?.data?.error_description ||
                          error?.response?.data?.message ||
                          error?.message || 
                          'Failed to accept invitation';
      toast.error(errorMessage);
    }
  };

  const completeRegistrationMutation = useMutation({
    mutationFn: async (data: InviteFormData) => {
      if (!token) throw new Error('No invitation token');

      const request: CompleteRegistrationRequest = {
        token,
        password: data.password,
        security_question: data.security_question,
        security_answer: data.security_answer,
        additional_profile: {
          mobile_number: data.mobile_number,
          registered_from: 'web',
          browser: navigator.userAgent,
        },
      };

      const response = await userInvitationService.completeRegistration(request);
      
      if (!response.success) {
        const errorMessage = response.error?.error_description || 
                           response.error?.message || 
                           response.description ||
                           'Registration failed';
        throw new Error(errorMessage);
      }
      
      return response.data;
    },
    onSuccess: (data) => {
      toast.success('Registration completed successfully!');
      setAcceptanceStatus('registered');
      
      // Show success message and redirect to login
      setTimeout(() => {
        navigate('/login', { 
          state: { 
            email: invitationData?.email,
            message: 'Your account has been created successfully. Please login with your credentials.' 
          } 
        });
      }, 2000);
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.error?.error_description ||
                          error?.response?.data?.error?.message ||
                          error?.response?.data?.error_description ||
                          error?.response?.data?.message ||
                          error?.message || 
                          'Registration failed. Please try again.';
      toast.error(errorMessage);
    },
  });

  const onSubmit = (data: InviteFormData) => {
    completeRegistrationMutation.mutate(data);
  };

  const getPasswordStrengthLabel = () => {
    if (passwordStrength < 30) return { label: 'Weak', color: 'text-red-600' };
    if (passwordStrength < 60) return { label: 'Fair', color: 'text-yellow-600' };
    if (passwordStrength < 80) return { label: 'Good', color: 'text-blue-600' };
    return { label: 'Strong', color: 'text-green-600' };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-indigo-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Verifying your invitation...</p>
        </div>
      </div>
    );
  }

  if (tokenError) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Invalid Invitation</h2>
          <p className="text-gray-600 mb-6">{tokenError}</p>
          <Link
            to="/login"
            className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            Go to Login
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    );
  }

  if (acceptanceStatus === 'registered') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Registration Complete!</h2>
          <p className="text-gray-600 mb-6">
            Your account has been successfully created. Redirecting to login...
          </p>
          <div className="flex justify-center">
            <Loader2 className="w-6 h-6 text-indigo-600 animate-spin" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center px-4 py-8">
      <div className="max-w-2xl w-full">
        {/* Company/Welcome Section */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Welcome to Skillzen</h1>
          <p className="text-gray-600">Complete your registration to get started</p>
        </div>

        {/* Main Card */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          {/* Invitation Info Header */}
          {invitationData && (
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-6 text-white">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                    <Mail className="w-6 h-6" />
                  </div>
                  <div>
                    <p className="text-sm opacity-90">Invitation for</p>
                    <p className="text-lg font-semibold">{invitationData.email}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm opacity-90">Invited by</p>
                  <p className="font-medium">{invitationData.invited_by_name}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white/10 rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <User className="w-4 h-4" />
                    <p className="text-sm opacity-90">Full Name</p>
                  </div>
                  <p className="font-medium">{invitationData.full_name}</p>
                </div>
                
                {invitationData.groups && invitationData.groups.length > 0 && (
                  <div className="bg-white/10 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <Users className="w-4 h-4" />
                      <p className="text-sm opacity-90">Groups</p>
                    </div>
                    <p className="font-medium">
                      {invitationData.groups.map(g => g.group_name).join(', ')}
                    </p>
                  </div>
                )}
              </div>

              {invitationData.expires_at && (
                <div className="mt-4 flex items-center gap-2 text-sm bg-white/10 rounded-lg px-3 py-2">
                  <Clock className="w-4 h-4" />
                  <span>
                    Invitation expires on {format(parseISO(invitationData.expires_at), 'PPP')}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Registration Form */}
          <form onSubmit={handleSubmit(onSubmit)} className="p-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center gap-2">
              <Shield className="w-5 h-5 text-indigo-600" />
              Set Your Password
            </h2>

            <div className="space-y-4">
              {/* Password Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Password *
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    {...register('password')}
                    className={`w-full px-4 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                      errors.password ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Enter your password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {errors.password && (
                  <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
                )}
                
                {/* Password Strength Indicator */}
                {password && (
                  <div className="mt-2">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-gray-500">Password strength</span>
                      <span className={`text-xs font-medium ${getPasswordStrengthLabel().color}`}>
                        {getPasswordStrengthLabel().label}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-1.5">
                      <div
                        className={`h-1.5 rounded-full transition-all ${
                          passwordStrength < 30
                            ? 'bg-red-500'
                            : passwordStrength < 60
                            ? 'bg-yellow-500'
                            : passwordStrength < 80
                            ? 'bg-blue-500'
                            : 'bg-green-500'
                        }`}
                        style={{ width: `${passwordStrength}%` }}
                      ></div>
                    </div>
                  </div>
                )}
              </div>

              {/* Confirm Password Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm Password *
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    {...register('confirm_password')}
                    className={`w-full px-4 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                      errors.confirm_password ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Re-enter your password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {errors.confirm_password && (
                  <p className="mt-1 text-sm text-red-600">{errors.confirm_password.message}</p>
                )}
              </div>

              {/* Phone Number Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number (Optional)
                </label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="tel"
                    {...register('mobile_number')}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="+1 (555) 000-0000"
                  />
                </div>
                {errors.mobile_number && (
                  <p className="mt-1 text-sm text-red-600">{errors.mobile_number.message}</p>
                )}
              </div>

              {/* Security Question */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Security Question (Optional)
                </label>
                <select
                  {...register('security_question')}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="">Select a security question</option>
                  {securityQuestions.map((question, idx) => (
                    <option key={idx} value={question}>
                      {question}
                    </option>
                  ))}
                </select>
              </div>

              {/* Security Answer */}
              {watch('security_question') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Security Answer *
                  </label>
                  <input
                    type="text"
                    {...register('security_answer')}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                      errors.security_answer ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Enter your answer"
                  />
                  {errors.security_answer && (
                    <p className="mt-1 text-sm text-red-600">{errors.security_answer.message}</p>
                  )}
                </div>
              )}
            </div>

            {/* Password Requirements */}
            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
              <p className="text-sm font-medium text-gray-700 mb-2">Password Requirements:</p>
              <ul className="space-y-1 text-sm text-gray-600">
                <li className="flex items-center gap-2">
                  <CheckCircle className={`w-4 h-4 ${password && password.length >= 8 ? 'text-green-500' : 'text-gray-400'}`} />
                  At least 8 characters
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className={`w-4 h-4 ${password && /[A-Z]/.test(password) ? 'text-green-500' : 'text-gray-400'}`} />
                  One uppercase letter
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className={`w-4 h-4 ${password && /[a-z]/.test(password) ? 'text-green-500' : 'text-gray-400'}`} />
                  One lowercase letter
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className={`w-4 h-4 ${password && /\d/.test(password) ? 'text-green-500' : 'text-gray-400'}`} />
                  One number
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className={`w-4 h-4 ${password && /[@$!%*?&]/.test(password) ? 'text-green-500' : 'text-gray-400'}`} />
                  One special character (@$!%*?&)
                </li>
              </ul>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={completeRegistrationMutation.isPending}
              className="w-full mt-6 flex items-center justify-center gap-2 px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {completeRegistrationMutation.isPending ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Completing Registration...
                </>
              ) : (
                <>
                  <UserCheck className="w-5 h-5" />
                  Complete Registration
                </>
              )}
            </button>

            {/* Login Link */}
            <p className="mt-6 text-center text-sm text-gray-600">
              Already have an account?{' '}
              <Link
                to="/login"
                className="font-medium text-indigo-600 hover:text-indigo-500"
              >
                Sign in
              </Link>
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}